import { mergeInventoryMetadata } from './inventory-shared-catalog';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import { AuraOwnerGateUnavailable } from './creature-status-reader';
import { constructPlayerPrefixFromBase, FROM_BASE_PLAYER_REMAINING_STAGES } from './from-base-player-prefix';
import { constructPlayerTierSkillsFromBase } from './from-base-player-skills';
import { compilePlayerInventoryFromBase, constructPlayerStatusEnvironmentFromBase, constructPlayerInventoryGrantsFromBase, constructPlayerPostInventoryFromBase } from './from-base-player-stages';
import { constructPlayerNestedGrantsFromBase } from './from-base-player-grants';
import { constructPlayerIntegersFromBase } from './from-base-player-integers';
import { constructPlayerForcedSkillsFromBase } from './from-base-player-forced-skills';
import { constructPlayerFullSkillEntriesFromBase } from './from-base-player-full-skills';
import { constructPlayerFeaturesFromBase } from './from-base-player-features';
import { constructPlayerDefenseSeedsFromBase, constructPlayerDefenseGrantsFromBase } from './from-base-player-defenses';
import { constructPlayerArmorClassFromBase } from './from-base-player-ac';
import { constructPlayerResourcesFromBase } from './from-base-player-resources';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { constructPlayerAttachedPictures } from './from-base-player-media';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { fromBaseClone } from './from-base-context';
import { putIntegerCompilePath } from './integer-compute-slots';
import { constructPlayerClustersFromBase } from '../port/k1-compile/from-base/player-clusters';
import { constructPlayerAbilitiesFromBase } from './from-base-player-abilities';
import { constructPlayerConsequenceDefaultsFromBase } from './from-base-player-consequences';
import { constructPlayerPostFinalizerFromBase } from './from-base-player-post';
import { normalizePlayerFromBase } from './from-base-player-normalization';
import { createNpcDefinitionFinalizer } from './from-base-npc-finalization';
import { constructPlayerActionBarsFromBase } from './from-base-player-display';
import { hydrateInventoryDefinitionMeshes } from './from-base-inventory-assets';
import { withActorAvatarBodies } from './from-base-avatar-body';
import { readItemField } from './item-field-reader';
import type { FromBaseDefinitionCache } from './from-base-definition-finalization';
import { createFromBaseStatusOriginInventory } from './from-base-status-references';
import { projectEncumbranceApplications, readEncumbranceApplications } from './encumbrance-application-plans';
import { fromBaseRecord } from './from-base-context';

export function compilePlayerFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, cache?: FromBaseDefinitionCache) {
    const initial = compilePlayerPassFromBase(base, metadata, cache);
    const applications = initial.computed ? readEncumbranceApplications(initial.computed) : [];
    if (!applications.length) return initial;
    const projected = compilePlayerPassFromBase(projectEncumbranceApplications(base, applications), metadata, cache);
    if (projected.computed && fromBaseRecord(projected.computed.computed)) {
        const computed = projected.computed.computed;
        if (readEncumbranceApplications(projected.computed).length) {
            projected.stagesMissing.push('raw-status-consequence-plans');
            projected.stageDiagnostics.push('raw-status-consequence-plans:encumbrance-projection-did-not-settle');
        }
        computed._cache = { ...(fromBaseRecord(computed._cache) ? computed._cache : {}), encumbrance_applications: applications };
    }
    return projected;
}

/** Run each native PC window once. A failed window preserves its predecessor
 * and names the exact unresolved dependency without restarting earlier stages. */
function compilePlayerPassFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, cache?: FromBaseDefinitionCache) {
    if (metadata.inventoryMetadata) {
        const finalize = createNpcDefinitionFinalizer(base as unknown as FromBaseRow, metadata, mergeInventoryMetadata(metadata.inventoryMetadata!, base.inventory_metadata), cache);
        metadata = { ...metadata, definitionLookup: id => finalize.definition(id, []) };
    }
    const prefix = constructPlayerPrefixFromBase(base, metadata);
    let row: FromBaseRow | undefined;
    let completed: string[] = [], missing: string[] = [...FROM_BASE_PLAYER_REMAINING_STAGES];
    let stage = 'player-source-initialization';
    const finish = (names: readonly string[], retired: readonly string[] = []) => {
        completed.push(...names);
        missing = missing.filter(name => !retired.includes(name));
    };
    const result = (diagnostic?: string) => ({ computed: row, stagesRun: [...new Set(completed)],
        stagesMissing: diagnostic ? [...new Set([stage, ...missing])] : missing,
        stageDiagnostics: diagnostic ? [diagnostic, ...missing] : missing });
    if (prefix.kind !== 'constructed-prefix') {
        stage = prefix.stage;
        return result(prefix.kind === 'missing-input' ? `${stage}:input:${prefix.dependency}:${prefix.id}` : `${stage}:${prefix.detail}`);
    }
    row = prefix.row; completed = [...prefix.completedStages]; missing = [...prefix.missingStages];
    let inventoryTree: ReturnType<typeof compilePlayerInventoryFromBase> | undefined;
    const inventory = () => inventoryTree ??= compilePlayerInventoryFromBase(base, metadata);
    const originInventory = createFromBaseStatusOriginInventory(base, () => {
        const tree = inventory();
        if (!tree.inventory || tree.missingStages.length) throw new FromBaseStageMissing('status-environment-layering',
            `inventory-origin:${tree.detail ?? tree.missingStages.join(',')}`);
        return tree.inventory;
    }, metadata.inventoryMetadata!);
    try {
        const meshes = readItemField({ meshes: [base.meshes ?? []] }, 'meshes');
        if (Array.isArray(meshes) && meshes.length) {
            const assets = mergeInventoryMetadata(metadata.inventoryMetadata!, base.inventory_metadata).definitionAssets;
            if (!assets) throw new FromBaseInputMissing('actor-mesh-metadata', base.entity_id);
            const hydrated = hydrateInventoryDefinitionMeshes(meshes, assets, metadata.resources.sizes
                .map(size => String(readItemField(size, 'item_id'))));
            if (hydrated.length) row.assets = { '3d': withActorAvatarBodies(hydrated, meshes,
                base.encounter_creatures, readItemField(base as unknown as FromBaseRow, 'avatar_wardrobe')) };
        }
        delete row._from_base_providers;
        Object.assign(row, constructPlayerActionBarsFromBase(base));
        stage = 'tiers-only-skills';
        if (!Array.isArray(metadata.classificationSizeTypes)) throw new FromBaseInputMissing('classification-size-metadata', base.entity_id);
        row = constructPlayerTierSkillsFromBase(base, metadata, row);
        finish(['definition-classification-size-defaults', 'pre-tiers-resource-defaults', stage], [stage]);
        stage = 'status-environment-layering';
        row = constructPlayerStatusEnvironmentFromBase(base, metadata, row, originInventory); finish([stage], [stage]);
        stage = 'inventory-construction-grants';
        const grants = constructPlayerInventoryGrantsFromBase(base, metadata, row, inventory());
        row = grants.row;
        if (grants.missingStages.length) throw new FromBaseStageMissing(stage, grants.missingStages.join(','));
        finish(['inventory-ability-grant-projection', stage], [stage]);
        stage = 'size-shapeshift-vitality';
        const post = constructPlayerPostInventoryFromBase(base, metadata, row, grants.inventory);
        row = post.row as FromBaseRow;
        finish(['ordinary-size-vitality-weight-projection', stage], [stage]);
        stage = 'nested-grants-override-prune';
        row = constructPlayerNestedGrantsFromBase(base, metadata, row).equipped; finish([stage], [stage]);
        stage = 'integer-rollup-full-skills';
        row = constructPlayerIntegersFromBase(base, metadata, row); finish(['integer-rollup-projection']);
        const forced = constructPlayerForcedSkillsFromBase(base, metadata, row); row = forced.row;
        finish(['passive-forced-skill-rolls']);
        row = constructPlayerFullSkillEntriesFromBase(base, metadata, row, forced.forced).row;
        finish(['full-skill-source-walks', 'full-skill-entry-projection']);
        row = constructPlayerFeaturesFromBase(base, metadata, row);
        finish(['natural-features-nested-skill-grants', stage], [stage]);
        stage = 'defenses-flags-ac-capacity';
        row = constructPlayerDefenseSeedsFromBase(base, metadata, row); finish(['root-permanent-status-defense-seeds']);
        row = constructPlayerDefenseGrantsFromBase(base, metadata, row); finish(['feat-passive-defense-flags']);
        row = constructPlayerArmorClassFromBase(base, metadata, row); finish(['root-tier-dedup-armor-class']);
        const { readers } = createFromBasePlayerGrantContext(base, metadata, row);
        const rawPictures = readers.itemField({ pictures: [base.pictures ?? null] }, 'pictures');
        if (!Array.isArray(rawPictures)) throw new FromBaseInputMissing('raw-player-pictures', base.entity_id);
        const input = fromBaseClone(row), pictures = constructPlayerAttachedPictures(rawPictures, 'player_character_picture', metadata.cdnUrl, readers.layerValue);
        if (pictures.length) putIntegerCompilePath(input, ['player_characters', 'pictures', 'player_character_picture'], pictures);
        row = constructPlayerResourcesFromBase(base, metadata, input, true);
        finish([stage, 'spellcasting-essence'], [stage]);
        stage = 'raw-status-consequence-plans';
        row = constructPlayerConsequenceDefaultsFromBase(base, metadata, row, post.burn); finish([stage], [stage]);
        stage = 'ability-spell-finalization';
        row = constructPlayerAbilitiesFromBase(base, metadata, row);
        finish([stage], [stage]);
        stage = 'post-finalizer-clusters';
        row = constructPlayerPostFinalizerFromBase(base, metadata, row);
        const clusters = constructPlayerClustersFromBase(base, metadata, row);
        row = clusters.row; finish(clusters.completedStages);
        delete row.pre_status_speed_layer_presence;
        finish([stage], [stage]);
        stage = 'save-section-definition-finalization';
        row = normalizePlayerFromBase(base, metadata, row); finish([stage, 'catalog-lean-normalization'], [stage, 'catalog-lean-normalization']);
        return result();
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return result(`${stage}:input:${error.dependency}:${error.id}`);
        if (error instanceof FromBaseStageMissing) { stage = error.stage; return result(`${stage}:${error.detail}`); }
        if (error instanceof AuraOwnerGateUnavailable) return result(`${stage}:${error.reason}`);
        throw error;
    }
}
